import { useEffect, useState } from "react";
import ChartWidget from "./ChartWidget";
import KpiWidget from "./KpiWidget";
import TableWidget from "./TableWidget";
import WidgetCard from "./WidgetCard";
import AgentMark from "./AgentMark";
import AnswerBubble from "./AnswerBubble";

function ToolChip({ chip }) {
  const mark = chip.status === "ok" ? "✓" : chip.status === "error" ? "✗" : "⋯";
  const cls =
    "chip" + (chip.status === "running" ? " running" : chip.status === "error" ? " err" : "");
  const tip = [
    chip.agent && `agent: ${chip.agent}`,
    chip.args && `args: ${chip.args}`,
    chip.result && `result: ${chip.result}`,
    chip.message && `error: ${chip.message}`,
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <span className={cls} title={tip || undefined}>
      {mark} {chip.tool}
    </span>
  );
}

function HandoffChip({ chip }) {
  return (
    <span className="chip handoff" title={`The supervisor handed this turn to ${chip.agent}`}>
      → {chip.agent}
    </span>
  );
}

/** Seconds since this turn started, ticking once a second while it runs.
 *
 * Worth the re-render: a hosted model can take 15-20s to produce its first token and returns the whole
 * reply in one piece, so without a moving number the UI is indistinguishable from a hung request. */
function Elapsed({ running }) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!running) return undefined;
    const started = Date.now();
    const t = setInterval(() => setSecs(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(t);
  }, [running]);
  if (!running || secs < 3) return null;  // below a few seconds it's noise, not information
  return <span className="elapsed">{secs}s</span>;
}

/**
 * Renders one assistant turn: the "thinking/routing" status line, delegation + tool chips (in arrival
 * order, grouped in one row like the original UI), then content blocks — text/chart/table/error — in
 * the exact order their events arrived. A speaker label appears above a text block only when more than
 * one specialist answered in this turn; a single-agent turn looks exactly like a plain chat bubble.
 */
export default function AssistantTurn({ turn }) {
  const speakers = new Set(turn.blocks.filter((b) => b.type === "text").map((b) => b.speaker));
  const multiSpeaker = speakers.size > 1;

  return (
    <div className="row assistant">
      <AgentMark busy={!turn.done} />
      <div className="turn-holder">
        {turn.status && (
          <div className="statusline">
            {turn.status}
            {!turn.done && (
              <span className="typing">
                <i></i>
                <i></i>
                <i></i>
              </span>
            )}
            <Elapsed running={!turn.done} />
          </div>
        )}

        {turn.chips.length > 0 && (
          <div className="chips">
            {turn.chips.map((chip, i) =>
              chip.kind === "handoff" ? <HandoffChip key={chip.id ?? i} chip={chip} /> : <ToolChip key={chip.id ?? i} chip={chip} />
            )}
          </div>
        )}

        {turn.blocks.map((block, i) => {
          if (block.type === "text") {
            return (
              <div key={i}>
                {multiSpeaker && <div className="who">{block.speaker}</div>}
                <AnswerBubble markdown={block.text} done={turn.done} />
              </div>
            );
          }
          if (block.type === "chart") return <ChartWidget key={i} spec={block.spec} />;
          if (block.type === "kpi") return <KpiWidget key={i} spec={block.spec} />;
          if (block.type === "table") return <TableWidget key={i} spec={block.spec} />;
          // A widget restored from a reopened conversation. Rendered as a WidgetCard (Visual/Table/
          // Narration tabs) rather than a bare chart, because once detached from the live turn the raw
          // numbers behind a chart are worth being able to open. It sits here inside .turn-holder so it
          // inherits the turn's width and centering — as a sibling of the message rows it had neither,
          // and drifted out of the column to the left edge of the scroller.
          if (block.type === "widget")
            return <WidgetCard key={i} kind={block.kind} spec={block.spec} />;
          if (block.type === "error") return <div key={i} className="errnote">{block.message}</div>;
          return null;
        })}
      </div>
    </div>
  );
}
